import { User, UserRoles } from 'types/user'
import axios from 'axios'
import { masterApiService, ssoAxios } from 'api/config'
import { store } from 'store/index'
import { settingsActions, settingsSelectors } from 'store/settings'
import { RegisterFormValues } from 'hooks/useAuthFlow'

type LoginResponse = {
  user: User
  token: string
  refreshToken: string | null
}

/**
 * Auto-login for `authMode === 'external'`. The parent host posts both an
 * `email` and a JWT `token` it has already issued for the user. We POST to
 * `/auth/login/external` (origin-allowlisted via services/altcha
 * `originAllowed`) only to resolve the user record; the token stored in
 * Redux — and used by the axios interceptor for every subsequent request —
 * is the one provided by the parent, NOT the one returned by the backend.
 *
 * The user's per-tenant profile (role/organization/external metadata) now travels
 * inside the enriched `token` itself — the server reads it from the token claims to
 * match areas (multi-area chats, /areas/tree/external). No profile is sent in the body.
 */
export const loginExternal = async (email: string, token: string): Promise<User> => {
  const state = store.getState()
  const tenantId = settingsSelectors.tenantId(state)
  if (!tenantId) {
    return Promise.reject(new Error('tenantId not available — TenantProvider must run first'))
  }

  const { data } = await ssoAxios.post<LoginResponse>(
    'auth/login/external',
    { email, tenantId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  store.dispatch(settingsActions.login({ user: data.user, token }))
  return data.user
}

export const loginUser = async (email: string, password: string) => {
  try {
    const { data } = await ssoAxios.post<LoginResponse>('auth', undefined, {
      headers: {
        Authorization: `Basic ${btoa(`${email}:${password}`)}`,
      },
    })

    const user = data.user

    if ([UserRoles.ADMIN, UserRoles.OPERATOR, UserRoles.SUPERVISOR].includes(user.role)) {
      return Promise.reject('userNotAllowedToLogin')
    }

    store.dispatch(settingsActions.login(data))
    return data.user
  } catch (e) {
    return Promise.reject(e)
  }
}

export const registerUser = async (form: RegisterFormValues) => {
  try {
    const state = store.getState()
    const tenantId = settingsSelectors.tenantId(state)
    await masterApiService.createItem('auth/register', {
      ...form,
      role: UserRoles.USER,
      tenantIds: tenantId ? [tenantId] : [],
    })
    return await loginUser(form.email, form.password)
  } catch (e) {
    return Promise.reject(e)
  }
}

export const resetPassword = async (email: string) => {
  try {
    await axios.post('password-resets', { email })
  } catch (e) {
    return Promise.reject(e)
  }
}

export type UpdateProfileParams = {
  name: string
  surname: string
}

export const updateProfile = async (params: UpdateProfileParams) => {
  try {
    const state = store.getState()
    const userId = state.settingsState.userDetails?._id
    if (!userId) {
      return Promise.reject('User not logged in')
    }
    const { data } = await axios.put<User>(`users/me`, params)
    store.dispatch(settingsActions.setUserDetails(data))
    return data
  } catch (e) {
    return Promise.reject(e)
  }
}
